import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { lazyWithReload } from '@/lib/lazy-with-reload';
import type { Editor } from '@tiptap/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxArchive,
  faClockRotateLeft,
  faCodeCompare,
  faCopy,
  faEye,
  faPaperPlane,
  faPenToSquare,
  faPlugCircleExclamation,
  faSpinner,
  faTrash,
  faUpload,
  faXmark,
  faCode,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import { TemplateEditor, insertVariable } from '@/components/communication/TemplateEditor';
import { VariableBrowser } from '@/components/communication/VariableBrowser';
import type { HtmlSourceEditorApi } from '@/components/communication/HtmlSourceEditor';

// CodeMirror is ~400 KB and only needed on the HTML Source tab — kept out of the
// main bundle so every other screen loads unaffected.
//
// `lazyWithReload` rather than bare `lazy`: a redeploy deletes the old hashed
// chunk, so a tab opened before the deploy would fail here with "Failed to fetch
// dynamically imported module" and dead-end on the error screen. It now reloads
// once onto the current build instead.
const HtmlSourceEditor = lazyWithReload(
  () =>
    import('@/components/communication/HtmlSourceEditor').then((m) => ({
      default: m.HtmlSourceEditor,
    })),
  'HtmlSourceEditor',
);
import { IssueList } from '@/components/communication/HtmlIssueList';
import {
  validateHtml,
  checkEmailSafety,
  getVisualEditorRisks,
  extractVariables,
  htmlToPlainText,
} from '@/lib/email-html';
import { normalizeEmailHtml } from '@/lib/email-html-normalize';
import { CategoryBadge, StatusBadge } from '@/components/communication/TemplateBadges';
import Swal from 'sweetalert2';
import { toast, swalConfirm } from '@/lib/swal';
import { cn } from '@/lib/cn';
import {
  activateMaster,
  archiveMaster,
  deleteMaster,
  disableMaster,
  getUsage,
  getVersions,
  rollbackMaster,
  updateMaster,
  type EmailTemplateMaster,
  type EmailTemplateMasterVersion,
  type TemplateUsage,
  type VariableDefinition,
} from '../api/platform-email-template.api';
import { PublishDialog } from './PublishDialog';
import { SendTestDialog } from './SendTestDialog';
import { CloneDialog } from './CloneDialog';
import { VersionCompare } from './VersionCompare';

/** Escapes text before it is interpolated into SweetAlert2 `html`. */
function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

type Tab = 'content' | 'source' | 'preview' | 'versions' | 'usage';

const TABS: { id: Tab; label: string; icon: typeof faEye }[] = [
  { id: 'content', label: 'Content', icon: faPenToSquare },
  { id: 'source', label: 'HTML Source', icon: faCode },
  { id: 'preview', label: 'Preview', icon: faEye },
  { id: 'versions', label: 'Versions', icon: faClockRotateLeft },
  { id: 'usage', label: 'Usage', icon: faPlugCircleExclamation },
];

const VIEWPORTS = {
  desktop: { label: 'Desktop', width: 900 },
  tablet: { label: 'Tablet', width: 768 },
  mobile: { label: 'Mobile', width: 390 },
} as const;
type ViewportKey = keyof typeof VIEWPORTS;

interface Props {
  template: EmailTemplateMaster | null;
  variables: VariableDefinition[];
  variablesLoading: boolean;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Master–detail drawer (COMMUNICATION_DESIGN.md §5.3). Usage, Last Published and
 * the version actor live here rather than in the list (§4.4).
 */
export function TemplateDetailDrawer({
  template,
  variables,
  variablesLoading,
  canManage,
  onClose,
  onChanged,
}: Props) {
  const [tab, setTab] = useState<Tab>('content');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [plainTextBody, setPlainTextBody] = useState('');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const [viewport, setViewport] = useState<ViewportKey>('desktop');

  const [versions, setVersions] = useState<EmailTemplateMasterVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [compareVersion, setCompareVersion] = useState<EmailTemplateMasterVersion | null>(null);

  const [usage, setUsage] = useState<TemplateUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [publishOpen, setPublishOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);

  /** Explicit opt-in to visual editing of HTML the visual editor would flatten. */
  const [visualOptIn, setVisualOptIn] = useState(false);
  const [autoPlainText, setAutoPlainText] = useState(true);
  const [sourceApi, setSourceApi] = useState<HtmlSourceEditorApi | null>(null);

  const open = template !== null;

  // Recomputed from the single htmlBody source of truth, so both editors and
  // the preview always agree on what is being validated.
  const visualRisks = useMemo(() => getVisualEditorRisks(htmlBody), [htmlBody]);
  const structureIssues = useMemo(() => validateHtml(htmlBody), [htmlBody]);
  const safetyIssues = useMemo(() => checkEmailSafety(htmlBody), [htmlBody]);
  const usedVariables = useMemo(() => extractVariables(htmlBody), [htmlBody]);

  const blockingErrors = useMemo(
    () => [...structureIssues, ...safetyIssues].filter((i) => i.severity === 'error'),
    [structureIssues, safetyIssues],
  );

  /**
   * Preview always renders the CURRENT draft — whichever editor produced it —
   * rather than the last saved copy. Substitution mirrors the backend renderer:
   * `{{key}}` is replaced by the registry sample value, and an unknown key is
   * left as-is rather than blanked (D-032).
   */
  const draftPreview = useMemo(() => {
    if (!template) return null;
    const samples = new Map(variables.map((v) => [v.key, v.exampleValue ?? '']));
    const unresolved = new Set<string>();
    const substitute = (text: string): string =>
      text.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, (match, key: string) => {
        const value = samples.get(key);
        if (value === undefined) {
          unresolved.add(key);
          return match;
        }
        return value;
      });

    const rendered = { subject: substitute(subject), htmlBody: substitute(htmlBody) };
    return { ...rendered, unresolved: [...unresolved] };
  }, [template, subject, htmlBody, variables]);

  /** Unlocking visual editing is destructive — make the cost explicit first. */
  const confirmVisualEdit = async () => {
    const ok = await swalConfirm({
      title: 'Flatten this template?',
      html:
        `<p style="margin:0 0 8px">The visual editor cannot represent ` +
        `<strong>${visualRisks.join(', ')}</strong>.</p>` +
        `<p style="margin:0">Your next edit in the visual tab will permanently remove them from ` +
        `this draft. The published version is not affected until you publish.</p>`,
      confirmText: 'I understand — edit visually',
      cancelText: 'Cancel',
    });
    if (ok) setVisualOptIn(true);
  };

  /**
   * Imported HTML is normalised before it reaches the editor: security removals
   * and Outlook-parity attributes only, never layout changes. What was altered
   * is shown before the author commits to it.
   */
  const confirmImport = async (imported: string) => {
    const {
      html: normalized,
      changes,
      unknownVariables,
    } = normalizeEmailHtml(
      imported,
      variables.map((v) => v.key),
    );

    const changeList =
      changes.length > 0
        ? '<p style="margin:12px 0 4px;font-weight:600">Normalised on import</p>' +
          '<ul style="text-align:left;margin:0;padding-left:18px">' +
          changes
            .map((c) => `<li>${escapeHtmlText(c.detail)} <em>(${c.count})</em></li>`)
            .join('') +
          '</ul>'
        : '<p style="margin:12px 0 0">No changes were needed — the document is already compatible.</p>';

    const unknownList =
      unknownVariables.length > 0
        ? '<p style="margin:12px 0 4px;font-weight:600;color:#b45309">Unrecognised variables</p>' +
          `<p style="margin:0">${escapeHtmlText(unknownVariables.map((v) => `{{${v}}}`).join(', '))}` +
          ' — these are not in the registry and will render literally.</p>'
        : '';

    const ok = await swalConfirm({
      title: 'Replace the HTML body?',
      html:
        '<p style="margin:0">The current HTML body will be replaced. This only changes your ' +
        'draft until you save.</p>' +
        changeList +
        unknownList,
      confirmText: 'Import',
    });
    if (ok) setHtmlBody(normalized);
  };

  /** Inserts a variable at the caret in the source editor. */
  const insertVariableAtSource = (key: string) => {
    sourceApi?.insertAtCursor(`{{${key}}}`);
  };

  // Reset editable state whenever a different template is opened.
  useEffect(() => {
    if (!template) return;
    setSubject(template.subject);
    setHtmlBody(template.htmlBody);
    setPlainTextBody(template.plainTextBody);
    // Open on HTML Source when the template is one the visual editor cannot
    // represent — that is the correct editor for it.
    setTab(getVisualEditorRisks(template.htmlBody).length > 0 ? 'source' : 'content');
    setVisualOptIn(false);
    // Auto plain text stays on unless this template already has hand-written
    // text that the generator would not reproduce.
    setAutoPlainText(
      !template.plainTextBody.trim() ||
        template.plainTextBody.trim() === htmlToPlainText(template.htmlBody).trim(),
    );
    setVersions([]);
    setUsage(null);
    setCompareVersion(null);
  }, [template]);

  const dirty =
    !!template &&
    (subject !== template.subject ||
      htmlBody !== template.htmlBody ||
      plainTextBody !== template.plainTextBody);

  const loadVersions = useCallback(async () => {
    if (!template) return;
    setVersionsLoading(true);
    try {
      setVersions(await getVersions(template.id));
    } catch {
      toast.error('Could not load version history.');
    } finally {
      setVersionsLoading(false);
    }
  }, [template]);

  const loadUsage = useCallback(async () => {
    if (!template) return;
    setUsageLoading(true);
    try {
      setUsage(await getUsage(template.id));
    } catch {
      toast.error('Could not load usage information.');
    } finally {
      setUsageLoading(false);
    }
  }, [template]);

  useEffect(() => {
    if (tab === 'versions' && versions.length === 0) void loadVersions();
    if (tab === 'usage' && usage === null) void loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, template?.id]);

  // Last published = newest version row; the list's updatedAt is last *modified* (§4.4).
  const lastPublished = useMemo(() => {
    if (versions.length === 0) return null;
    return versions.reduce((newest, v) => (v.createdAt > newest.createdAt ? v : newest));
  }, [versions]);

  if (!open || !template) return null;

  const runAction = async (fn: () => Promise<unknown>, successMsg: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      onChanged();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Action failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save stores ONE HTML document — the single `htmlBody` both editors bind to.
   * Structural errors block the save; HTML is never silently repaired.
   * Saving a draft does not create a version (only Publish does).
   */
  const handleSave = async () => {
    const structural = structureIssues.filter((i) => i.severity === 'error');
    if (structural.length > 0) {
      await Swal.fire({
        icon: 'error',
        title: 'Fix the HTML before saving',
        html:
          '<ul style="text-align:left;margin:0;padding-left:18px">' +
          structural
            .slice(0, 8)
            .map(
              (i) =>
                `<li>${escapeHtmlText(i.message)}${
                  i.line !== undefined ? ` <em>(line ${i.line})</em>` : ''
                }</li>`,
            )
            .join('') +
          (structural.length > 8 ? `<li>…and ${structural.length - 8} more</li>` : '') +
          '</ul>',
        confirmButtonText: 'Back to editor',
        confirmButtonColor: 'hsl(74 40% 40%)',
        customClass: { popup: 'rounded-xl' },
      });
      setTab('source');
      return;
    }

    const dangerous = safetyIssues.filter((i) => i.severity === 'error');
    if (dangerous.length > 0) {
      await Swal.fire({
        icon: 'error',
        title: 'Unsafe content for email',
        html:
          '<ul style="text-align:left;margin:0;padding-left:18px">' +
          dangerous.map((i) => `<li>${escapeHtmlText(i.message)}</li>`).join('') +
          '</ul>',
        confirmButtonText: 'Back to editor',
        confirmButtonColor: 'hsl(74 40% 40%)',
        customClass: { popup: 'rounded-xl' },
      });
      setTab('source');
      return;
    }

    // Auto-generated plain text is produced at save time from the final HTML.
    const resolvedPlainText = autoPlainText ? htmlToPlainText(htmlBody) : plainTextBody;

    setSaving(true);
    try {
      await updateMaster(template.id, { subject, htmlBody, plainTextBody: resolvedPlainText });
      if (autoPlainText) setPlainTextBody(resolvedPlainText);
      toast.success('Draft saved — publish to release it as a new version');
      onChanged();
      setVersions([]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Publish gate. Dangerous constructs are rejected outright; unsupported ones
   * are surfaced as a warning the publisher must explicitly accept.
   */
  const handlePublishClick = async () => {
    if (blockingErrors.length > 0) {
      await Swal.fire({
        icon: 'error',
        title: 'Cannot publish',
        html:
          '<ul style="text-align:left;margin:0;padding-left:18px">' +
          blockingErrors.map((i) => `<li>${escapeHtmlText(i.message)}</li>`).join('') +
          '</ul>',
        confirmButtonText: 'Back to editor',
        confirmButtonColor: 'hsl(74 40% 40%)',
        customClass: { popup: 'rounded-xl' },
      });
      setTab('source');
      return;
    }

    const warnings = [...structureIssues, ...safetyIssues].filter((i) => i.severity === 'warning');
    if (warnings.length > 0) {
      const ok = await swalConfirm({
        title: 'Publish with warnings?',
        html:
          '<p style="margin:0 0 8px">These will not render reliably in email clients:</p>' +
          '<ul style="text-align:left;margin:0;padding-left:18px">' +
          warnings
            .slice(0, 8)
            .map((i) => `<li>${escapeHtmlText(i.message)}</li>`)
            .join('') +
          (warnings.length > 8 ? `<li>…and ${warnings.length - 8} more</li>` : '') +
          '</ul>',
        confirmText: 'Publish anyway',
      });
      if (!ok) return;
    }

    setPublishOpen(true);
  };

  const handleArchive = async () => {
    const current = usage ?? (await getUsage(template.id).catch(() => null));
    const deps = current?.usedBy ?? [];
    const warning =
      deps.length > 0
        ? `This template is still triggered by: ${deps.join(', ')}. Archiving it will stop those emails from using it.`
        : 'This template has no registered trigger points.';
    const ok = await swalConfirm({
      title: 'Archive template?',
      text: warning,
      confirmText: 'Archive',
    });
    if (!ok) return;
    await runAction(() => archiveMaster(template.id), 'Template archived');
  };

  const handleDelete = async () => {
    const ok = await swalConfirm({
      title: 'Delete permanently?',
      text: `"${template.name}" will be deleted. This cannot be undone, and the backend will refuse if any tenant still references it.`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    await runAction(async () => {
      await deleteMaster(template.id);
      onClose();
    }, 'Template deleted');
  };

  const handleRollback = async (version: number) => {
    const ok = await swalConfirm({
      title: `Roll back to v${version}?`,
      text: 'This creates a new version containing the older content. Nothing is overwritten.',
      confirmText: 'Roll back',
    });
    if (!ok) return;
    await runAction(async () => {
      await rollbackMaster(template.id, version);
      setVersions([]);
      await loadVersions();
    }, `Rolled back to v${version}`);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />

      <aside
        role="dialog"
        aria-label={`${template.name} details`}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-card shadow-2xl sm:max-w-3xl"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-3.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-semibold text-foreground">{template.name}</h2>
              <StatusBadge status={template.status} isArchived={template.isArchived} />
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] text-muted-foreground">
                {template.templateKey}
              </span>
              <CategoryBadge category={template.category} />
              <span className="font-mono text-[11px] text-muted-foreground">
                v{template.currentVersion}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </header>

        {/* Tabs */}
        <nav className="flex gap-1 border-b border-border px-3 py-1.5">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                tab === t.id
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <FontAwesomeIcon icon={t.icon} className="h-3 w-3" />
              {t.label}
            </button>
          ))}
        </nav>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {tab === 'content' && (
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="min-w-0 flex-1 space-y-3">
                <FormField label="Subject" htmlFor="master-subject">
                  <Input
                    id="master-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={!canManage || saving}
                    placeholder="Email subject line"
                  />
                </FormField>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    HTML Body
                  </label>

                  {/* The visual editor cannot represent table layouts, images or
                      inline styles — its schema simply drops them. Rather than
                      silently destroying the template the first time someone
                      types, it stays read-only until explicitly unlocked. */}
                  {visualRisks.length > 0 && !visualOptIn ? (
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
                      <div className="flex items-start gap-2.5">
                        <FontAwesomeIcon
                          icon={faTriangleExclamation}
                          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500"
                        />
                        <div className="min-w-0 text-sm">
                          <p className="font-medium text-foreground">
                            This template needs the HTML Source editor
                          </p>
                          <p className="mt-1 text-muted-foreground">
                            It uses {visualRisks.join(', ')}, which the visual editor cannot
                            represent — editing here would permanently flatten the layout.
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button type="button" size="sm" onClick={() => setTab('source')}>
                              <FontAwesomeIcon icon={faCode} className="mr-1.5 h-3 w-3" />
                              Edit HTML Source
                            </Button>
                            {canManage && (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={() => void confirmVisualEdit()}
                              >
                                Edit visually anyway
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 max-h-40 overflow-auto rounded border border-border bg-muted/30 p-2">
                        <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-muted-foreground">
                          {htmlBody.slice(0, 1200)}
                          {htmlBody.length > 1200 ? '\n…' : ''}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <>
                      {visualRisks.length > 0 && visualOptIn && (
                        <p className="mb-1.5 rounded border border-amber-500/40 bg-amber-500/5 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-500">
                          Visual editing unlocked — {visualRisks.join(', ')} will be lost on your
                          next edit here.
                        </p>
                      )}
                      <TemplateEditor
                        value={htmlBody}
                        onChange={setHtmlBody}
                        disabled={!canManage || saving}
                        onEditorReady={setEditor}
                      />
                    </>
                  )}
                </div>

                <FormField label="Plain Text Body" htmlFor="master-plaintext">
                  <label className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={autoPlainText}
                      onChange={(e) => setAutoPlainText(e.target.checked)}
                      disabled={!canManage || saving}
                      className="h-3.5 w-3.5 accent-primary"
                    />
                    Auto-generate plain text from HTML on save
                  </label>
                  <textarea
                    id="master-plaintext"
                    value={autoPlainText ? htmlToPlainText(htmlBody) : plainTextBody}
                    onChange={(e) => setPlainTextBody(e.target.value)}
                    disabled={!canManage || saving || autoPlainText}
                    rows={5}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    placeholder="Plain-text fallback"
                  />
                </FormField>
              </div>

              {/* Variable panel (§5.4) */}
              <div className="lg:w-72 lg:shrink-0">
                <p className="mb-1.5 text-sm font-medium text-foreground">Variables</p>
                <VariableBrowser
                  variables={variables}
                  loading={variablesLoading}
                  onInsert={(key) => insertVariable(editor, key)}
                />
              </div>
            </div>
          )}

          {tab === 'source' && (
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="min-w-0 flex-1 space-y-3">
                <FormField label="Subject" htmlFor="master-subject-src">
                  <Input
                    id="master-subject-src"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={!canManage || saving}
                    placeholder="Email subject line"
                  />
                </FormField>

                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <label className="text-sm font-medium text-foreground">HTML Body</label>
                    <span className="text-xs text-muted-foreground">
                      {htmlBody.length.toLocaleString()} chars · {usedVariables.length} variable
                      {usedVariables.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <Suspense
                    fallback={
                      <div className="flex h-[420px] items-center justify-center rounded-md border border-input">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          className="h-5 w-5 animate-spin text-muted-foreground"
                        />
                      </div>
                    }
                  >
                    <HtmlSourceEditor
                      value={htmlBody}
                      onChange={setHtmlBody}
                      disabled={!canManage || saving}
                      onImport={(imported) => void confirmImport(imported)}
                      onReady={setSourceApi}
                    />
                  </Suspense>
                </div>

                <IssueList
                  title="Validation"
                  issues={structureIssues}
                  emptyMessage="No structural problems found."
                />
                <IssueList
                  title="Email compatibility"
                  issues={safetyIssues}
                  emptyMessage="No email-client problems found."
                />
              </div>

              <div className="lg:w-72 lg:shrink-0">
                <p className="mb-1.5 text-sm font-medium text-foreground">Variables</p>
                <VariableBrowser
                  variables={variables}
                  loading={variablesLoading}
                  onInsert={(key) => insertVariableAtSource(key)}
                />
              </div>
            </div>
          )}

          {tab === 'preview' && (
            <div className="space-y-3">
              <div className="flex items-center gap-1.5">
                {(Object.keys(VIEWPORTS) as ViewportKey[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setViewport(key)}
                    className={cn(
                      'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                      viewport === key
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {VIEWPORTS[key].label}
                  </button>
                ))}
                {/* No Refresh button: the preview is derived from editor state,
                    so it is always current by construction. */}
                <span className="ml-auto text-xs text-muted-foreground">
                  Live preview of the current draft
                </span>
              </div>

              {draftPreview ? (
                <div className="space-y-2">
                  {dirty && (
                    <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
                      Previewing your unsaved draft — this is the HTML currently in the editor, not
                      the saved version.
                    </p>
                  )}
                  <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Subject
                    </p>
                    <p className="text-sm text-foreground">{draftPreview.subject}</p>
                  </div>
                  <div className="flex justify-center overflow-x-auto rounded-lg border border-border bg-muted/20 p-3">
                    <iframe
                      title="Email preview"
                      srcDoc={draftPreview.htmlBody}
                      sandbox=""
                      style={{ width: VIEWPORTS[viewport].width }}
                      className="h-[520px] max-w-full rounded border border-border bg-white"
                    />
                  </div>
                  {draftPreview.unresolved.length > 0 && (
                    <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                      No sample value for:{' '}
                      <span className="font-mono">{draftPreview.unresolved.join(', ')}</span> —
                      these render as-is here but resolve at send time.
                    </p>
                  )}
                </div>
              ) : (
                <p className="py-16 text-center text-sm text-muted-foreground">
                  No preview available.
                </p>
              )}
            </div>
          )}

          {tab === 'versions' && (
            <div className="space-y-3">
              {lastPublished && (
                <p className="text-xs text-muted-foreground">
                  Last published{' '}
                  <span className="text-foreground">
                    {new Date(lastPublished.createdAt).toLocaleString()}
                  </span>{' '}
                  (v{lastPublished.versionNumber})
                </p>
              )}

              {versionsLoading ? (
                <div className="flex justify-center py-16">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-5 w-5 animate-spin text-muted-foreground"
                  />
                </div>
              ) : versions.length === 0 ? (
                <p className="py-16 text-center text-sm text-muted-foreground">
                  No published versions yet.
                </p>
              ) : (
                <ol className="relative space-y-3 border-l border-border pl-5">
                  {versions.map((v) => (
                    <li key={v.id} className="relative">
                      <span className="absolute -left-[23px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                      <div className="rounded-lg border border-border bg-card p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-mono text-xs font-semibold text-foreground">
                            v{v.versionNumber}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {new Date(v.createdAt).toLocaleString()}
                          </span>
                        </div>

                        {v.releaseNotes?.summary && (
                          <p className="mt-1.5 text-xs text-foreground">{v.releaseNotes.summary}</p>
                        )}

                        {v.releaseNotes && <ReleaseNoteDetail notes={v.releaseNotes} />}

                        {v.changedByName && (
                          <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">
                            by {v.changedByName}
                          </p>
                        )}

                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setCompareVersion(v)}
                          >
                            <FontAwesomeIcon icon={faCodeCompare} className="mr-1.5 h-3 w-3" />
                            Compare
                          </Button>
                          {canManage && v.versionNumber !== template.currentVersion && (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={busy}
                              onClick={() => void handleRollback(v.versionNumber)}
                            >
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {tab === 'usage' && (
            <div className="space-y-3">
              {usageLoading ? (
                <div className="flex justify-center py-16">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-5 w-5 animate-spin text-muted-foreground"
                  />
                </div>
              ) : !usage || usage.usedBy.length === 0 ? (
                <div className="rounded-lg border border-border bg-muted/20 p-4 text-center">
                  <p className="text-sm text-foreground">No registered trigger points</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Nothing in the platform currently sends this template. It is safe to archive or
                    delete.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Triggered by
                    </p>
                    <ul className="space-y-1">
                      {usage.usedBy.map((u) => (
                        <li
                          key={u}
                          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
                        >
                          {u}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Dependent modules
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {usage.modules.map((m) => (
                        <span
                          key={m}
                          className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer actions (§6) — hidden entirely for read-only roles */}
        {canManage && (
          <footer className="flex flex-wrap items-center gap-1.5 border-t border-border px-5 py-3">
            <Button size="sm" onClick={() => void handleSave()} disabled={!dirty || saving}>
              {saving && (
                <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
              )}
              Save
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void handlePublishClick()}
              disabled={busy || dirty}
              title={dirty ? 'Save your changes before publishing' : 'Publish a new version'}
            >
              <FontAwesomeIcon icon={faUpload} className="mr-1.5 h-3 w-3" />
              Publish
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setTestOpen(true)} disabled={busy}>
              <FontAwesomeIcon icon={faPaperPlane} className="mr-1.5 h-3 w-3" />
              Send Test
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setCloneOpen(true)}
              disabled={busy}
            >
              <FontAwesomeIcon icon={faCopy} className="mr-1.5 h-3 w-3" />
              Clone
            </Button>

            <span className="mx-1 h-5 w-px bg-border" />

            {template.status === 'DRAFT' && (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  void runAction(() => activateMaster(template.id), 'Template activated')
                }
              >
                Activate
              </Button>
            )}
            {template.status === 'ACTIVE' && (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={async () => {
                  const ok = await swalConfirm({
                    title: 'Disable template?',
                    text: 'Tenants will stop receiving updates for this template.',
                    confirmText: 'Disable',
                  });
                  if (ok) await runAction(() => disableMaster(template.id), 'Template disabled');
                }}
              >
                Disable
              </Button>
            )}
            {template.status === 'DISABLED' && !template.isArchived && (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => void handleArchive()}
              >
                <FontAwesomeIcon icon={faBoxArchive} className="mr-1.5 h-3 w-3" />
                Archive
              </Button>
            )}

            <Button
              size="sm"
              variant="secondary"
              className="ml-auto text-destructive hover:text-destructive"
              disabled={busy}
              onClick={() => void handleDelete()}
            >
              <FontAwesomeIcon icon={faTrash} className="mr-1.5 h-3 w-3" />
              Delete
            </Button>
          </footer>
        )}
      </aside>

      <PublishDialog
        open={publishOpen}
        template={template}
        variables={variables}
        onClose={() => setPublishOpen(false)}
        onPublished={() => {
          setPublishOpen(false);
          setVersions([]);
          onChanged();
        }}
      />

      <SendTestDialog open={testOpen} template={template} onClose={() => setTestOpen(false)} />

      <CloneDialog
        open={cloneOpen}
        template={template}
        onClose={() => setCloneOpen(false)}
        onCloned={() => {
          setCloneOpen(false);
          onChanged();
        }}
      />

      <VersionCompare
        version={compareVersion}
        current={template}
        onClose={() => setCompareVersion(null)}
      />
    </>
  );
}

function ReleaseNoteDetail({
  notes,
}: {
  notes: NonNullable<EmailTemplateMasterVersion['releaseNotes']>;
}) {
  const groups: { label: string; items: string[] }[] = [
    { label: 'Added', items: notes.featuresAdded ?? [] },
    { label: 'Removed', items: notes.featuresRemoved ?? [] },
    { label: 'Variables added', items: notes.variablesAdded ?? [] },
    { label: 'Variables removed', items: notes.variablesRemoved ?? [] },
  ].filter((g) => g.items.length > 0);

  if (groups.length === 0 && !notes.breakingChanges) return null;

  return (
    <div className="mt-2 space-y-1.5">
      {groups.map((g) => (
        <div key={g.label}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {g.label}
          </p>
          <ul className="ml-3 list-disc text-[11px] text-foreground">
            {g.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
      {notes.breakingChanges && (
        <div className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            Breaking changes
          </p>
          <p className="text-[11px] text-foreground">{notes.breakingChanges}</p>
        </div>
      )}
    </div>
  );
}
